// ─────────────────────────────────────────────────────────────────
// QV1: vilt i det Q hör eller det som skrivs i jaktchatten ("skjuten dov, den ligger",
// "älgtjur", "vildsvin årsgris", "dov kalv") → art (familj), typ (kön/ålder, skjutmenyns
// underkategori) och vad som hände (fällt, skadskjutet, skott, obs).
//
// Samma fil finns i apps/web/src/heardGame.ts och apps/api/src/lib/jakttider/heardGame.ts
// (API:ts test kontrollerar att de är lika) – ändra båda.
// ─────────────────────────────────────────────────────────────────

export type HeardOutcome = 'fallt' | 'skadskjutet' | 'skott' | 'obs';

export interface HeardGame {
  /** Familjenyckel, samma som familyKey/wildlifeData: 'dovhjort', 'vildsvin' … */
  family: string;
  /** Visningsnamn för familjen: 'Dovhjort'. */
  label: string;
  /** Skjutmenyns underkategori (SUBCATS) när typen räcker för att välja en: 'Dovkalv', 'Sugga', 'Ko'. */
  subcat?: string;
  /** Typen som hördes, för text: 'kalv', 'tjur', 'sugga', 'årsgris'. */
  typ?: string;
  kon?: 'Hane' | 'Hona';
  alder?: 'Ungdjur' | 'Vuxet';
}

const LABELS: Record<string, string> = {
  alg: 'Älg', kronhjort: 'Kronhjort', dovhjort: 'Dovhjort', vildsvin: 'Vildsvin', radjur: 'Rådjur', mufflon: 'Mufflon',
  bjorn: 'Björn', varg: 'Varg', lodjur: 'Lodjur', jarv: 'Järv', hare: 'Hare', vildkanin: 'Vildkanin', fasan: 'Fasan',
  orre: 'Orre', tjader: 'Tjäder', skogsfagel: 'Skogsfågel', kanadagas: 'Kanadagås', andfagel: 'Andfågel', rav: 'Räv',
  gravling: 'Grävling', baver: 'Bäver', mink: 'Mink', mardhund: 'Mårdhund', mard: 'Mård', iller: 'Iller',
};

/** Gemener utan diakritiska tecken och skiljetecken: "Älgtjur, död!" → "algtjur dod". */
export function heardNorm(s: string): string {
  return String(s ?? '').toLowerCase().normalize('NFD').replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}>]+/gu, ' ').trim();
}

// Böjningar: bestämd form, plural, genitiv. "er" ingår inte ("hind" → "hinder").
const S = ['', 'en', 'et', 'n', 't', 'ar', 'arna', 'or', 'orna', 'na', 's', 'ens', 'ets', 'ns'];
const NEUTER = ['', 'et', 'en', 'ets', 's']; // rådjur, vildsvin, kid, lamm
type Forms = string[];
function forms(stem: string, suf: string[] = S): Forms { return suf.map(x => stem + x); }

/** Ord som ensamma betyder en art. */
const SPECIES_WORDS: [string, Forms][] = [
  ['alg', forms('alg', ['', 'en', 'ar', 'arna', 'ens', 's'])],
  ['dovhjort', [...forms('dovhjort'), ...forms('dovjort'), ...forms('dovvilt', NEUTER), 'dov', 'dovar', 'dovarna']],
  ['kronhjort', [...forms('kronhjort'), ...forms('kronvilt', NEUTER)]],
  ['radjur', [...forms('radjur', NEUTER), 'radjuren', 'radjurens']],
  ['vildsvin', [...forms('vildsvin', NEUTER), 'vildsvinen', ...forms('svin', NEUTER), 'svinen', ...forms('vildgris', ['', 'en', 'ar', 'arna']), ...forms('gris', ['', 'en', 'ar', 'arna'])]],
  ['mufflon', forms('mufflon', ['', 'en', 'er', 'erna', 'far', 'faret'])],
  ['bjorn', forms('bjorn', ['', 'en', 'ar', 'arna'])],
  ['varg', forms('varg', ['', 'en', 'ar', 'arna'])],
  ['lodjur', [...forms('lodjur', NEUTER), 'lodjuren', 'lo', 'lon']],
  ['jarv', forms('jarv', ['', 'en', 'ar'])],
  ['hare', ['hare', 'haren', 'harar', 'hararna', 'harens', 'skogshare', 'skogsharen', 'falthare', 'falthararen', 'falthararna']],
  ['vildkanin', forms('vildkanin', ['', 'en', 'er', 'erna'])],
  ['fasan', forms('fasan', ['', 'en', 'er', 'erna', 'tupp', 'tuppen', 'hona', 'honan'])],
  ['orre', ['orre', 'orren', 'orrar', 'orrtupp', 'orrtuppen', 'orrhona']],
  ['tjader', ['tjader', 'tjadern', 'tjadrar', 'tjadertupp', 'tjaderhona']],
  ['skogsfagel', ['skogsfagel', 'skogsfageln', 'skogsfaglar', 'jarpe', 'jarpen', 'jarpar', 'ripa', 'ripan', 'ripor']],
  ['kanadagas', ['kanadagas', 'kanadagasen', 'kanadagass', 'gragas', 'gragasen', 'gragass', 'blasgas', 'gas', 'gasen', 'gass', 'gassen']],
  ['andfagel', ['andfagel', 'andfaglar', 'grasand', 'grasanden', 'grasander', 'kricka', 'krickan', 'krickor', 'blasand', 'knipa', 'knipor', 'vigg', 'viggar', 'and', 'anden', 'ander', 'anderna']],
  ['rav', forms('rav', ['', 'en', 'ar', 'arna', 'ens'])],
  ['gravling', forms('gravling', ['', 'en', 'ar', 'arna'])],
  ['baver', ['baver', 'bavern', 'bavrar', 'bavrarna']],
  ['mink', forms('mink', ['', 'en', 'ar', 'arna'])],
  ['mardhund', forms('mardhund', ['', 'en', 'ar', 'arna'])],
  ['mard', forms('mard', ['', 'en', 'ar'])],
  ['iller', ['iller', 'illern', 'illrar']],
];

/** Förled i sammansättningar och tvåordsformer ("dov kalv", "rå bock", "vildsvins galt"). */
const PREFIXES: [string, string][] = [
  ['dovhjort', 'dovhjort'], ['dovjort', 'dovhjort'], ['dov', 'dovhjort'],
  ['kronhjort', 'kronhjort'], ['kron', 'kronhjort'],
  ['radjur', 'radjur'], ['ra', 'radjur'],
  ['vildsvin', 'vildsvin'], ['vild', 'vildsvin'],
  ['mufflon', 'mufflon'], ['alg', 'alg'],
];
/** Förled som ensamma inte är en art men går ihop med nästa ord ("rå bock", "kron hind"). */
const BARE_PREFIX: Record<string, string> = { kron: 'kronhjort', ra: 'radjur' };

type Typ = { typ: string; kon?: 'Hane' | 'Hona'; alder?: 'Ungdjur' | 'Vuxet'; sub?: Record<string, string> };
/** Typord (kön/ålder) och vilken underkategori de ger per art. */
const TYPES: [Forms, Typ][] = [
  [forms('kalv', ['', 'en', 'ar', 'arna', 'ens']), { typ: 'kalv', alder: 'Ungdjur', sub: { alg: 'Kalv', kronhjort: 'Kalv', dovhjort: 'Dovkalv', radjur: 'Råkid' } }],
  [['ko', 'kon', 'kor', 'korna'], { typ: 'ko', kon: 'Hona', alder: 'Vuxet', sub: { alg: 'Ko', kronhjort: 'Hind', dovhjort: 'Dovhind' } }],
  [forms('kvig', ['a', 'an', 'or', 'orna']), { typ: 'kviga', kon: 'Hona', alder: 'Ungdjur', sub: { alg: 'Kviga' } }],
  [forms('tjur', ['', 'en', 'ar', 'arna', 'ens']), { typ: 'tjur', kon: 'Hane', alder: 'Vuxet' }],
  [forms('hind', ['', 'en', 'ar', 'arna', 'ens']), { typ: 'hind', kon: 'Hona', alder: 'Vuxet', sub: { kronhjort: 'Hind', dovhjort: 'Dovhind' } }],
  [forms('hjort', ['', 'en', 'ar', 'arna']), { typ: 'hjort' }],
  [forms('bock', ['', 'en', 'ar', 'arna', 'ens']), { typ: 'bock', kon: 'Hane', alder: 'Vuxet' }],
  [['get', 'geten', 'getter', 'getterna', 'getens'], { typ: 'get', kon: 'Hona', alder: 'Vuxet', sub: { radjur: 'Råget' } }],
  [forms('kid', NEUTER), { typ: 'kid', alder: 'Ungdjur', sub: { radjur: 'Råkid' } }],
  [forms('spets', ['', 'en', 'ar', 'arna']), { typ: 'spets', kon: 'Hane', alder: 'Vuxet', sub: { kronhjort: 'Spets', dovhjort: 'Dovspets', radjur: 'Råbock spets', alg: 'Tjur 2–4 taggar' } }],
  [['spetstjur', 'spetstjuren'], { typ: 'spetstjur', kon: 'Hane', alder: 'Vuxet', sub: { alg: 'Tjur 2–4 taggar' } }],
  [['spetsbock', 'spetsbocken', 'spetshjort', 'spetshjorten'], { typ: 'spets', kon: 'Hane', alder: 'Vuxet', sub: { kronhjort: 'Spets', dovhjort: 'Dovspets', radjur: 'Råbock spets', alg: 'Tjur 2–4 taggar' } }],
  [['helskovel', 'helskoveln', 'skovel', 'skoveln', 'skovelhjort', 'skovelhjorten'], { typ: 'skovel', kon: 'Hane', alder: 'Vuxet', sub: { dovhjort: 'Dovhelskovel' } }],
  [['halvskovel', 'halvskoveln'], { typ: 'halvskovel', kon: 'Hane', alder: 'Vuxet', sub: { dovhjort: 'Dovhalvskovel' } }],
  [['kapitalhjort', 'kapitalhjorten', 'kapital'], { typ: 'kapitalhjort', kon: 'Hane', alder: 'Vuxet', sub: { kronhjort: 'Kapitalhjort' } }],
  [['medaljbock', 'medaljbocken', 'medalj'], { typ: 'medaljbock', kon: 'Hane', alder: 'Vuxet', sub: { radjur: 'Råbock medalj' } }],
  [forms('sugg', ['', 'a', 'an', 'or', 'orna', 'ans']), { typ: 'sugga', kon: 'Hona', alder: 'Vuxet', sub: { vildsvin: 'Sugga' } }],
  [forms('galt', ['', 'en', 'ar', 'arna', 'ens']), { typ: 'galt', kon: 'Hane', alder: 'Vuxet', sub: { vildsvin: 'Galt' } }],
  [forms('arsgris', ['', 'en', 'ar', 'arna']), { typ: 'årsgris', alder: 'Ungdjur', sub: { vildsvin: 'Årsgris' } }],
  [[...forms('kulting', ['', 'en', 'ar', 'arna']), ...forms('kolting', ['', 'en', 'ar', 'arna'])], { typ: 'kulting', alder: 'Ungdjur', sub: { vildsvin: 'Årsgris' } }],
  [forms('smagris', ['', 'en', 'ar', 'arna']), { typ: 'smågris', alder: 'Ungdjur', sub: { vildsvin: 'Årsgris' } }],
  [forms('bagg', ['e', 'en', 'ar', 'arna']), { typ: 'bagge', kon: 'Hane', alder: 'Vuxet', sub: { mufflon: 'Mindre bagge' } }],
  [['kapitalbagge', 'kapitalbaggen'], { typ: 'kapitalbagge', kon: 'Hane', alder: 'Vuxet', sub: { mufflon: 'Kapital Bagge' } }],
  [forms('tack', ['a', 'an', 'or', 'orna']), { typ: 'tacka', kon: 'Hona', alder: 'Vuxet', sub: { mufflon: 'Hona' } }],
  [forms('lamm', NEUTER), { typ: 'lamm', alder: 'Ungdjur', sub: { mufflon: 'Lamm' } }],
  [forms('han', ['e', 'en', 'ar', 'arna']), { typ: 'hane', kon: 'Hane' }],
  [forms('hon', ['a', 'an', 'or', 'orna']), { typ: 'hona', kon: 'Hona', sub: { mufflon: 'Hona' } }],
  [forms('ung', ['e', 'en', 'ar', 'arna']), { typ: 'unge', alder: 'Ungdjur' }],
];

/** Typord som ensamma pekar ut arten ("sugga", "kulting", "kviga"). Övriga ("kalv", "hind", "bock") kräver arten. */
const TYPE_ONLY_FAMILY: Record<string, string> = {
  sugga: 'vildsvin', galt: 'vildsvin', årsgris: 'vildsvin', kulting: 'vildsvin', smågris: 'vildsvin',
  kviga: 'alg', tjur: 'alg', medaljbock: 'radjur', kapitalhjort: 'kronhjort', kapitalbagge: 'mufflon',
  halvskovel: 'dovhjort', skovel: 'dovhjort', spetstjur: 'alg',
};
/** Typer som inte hör till arten ("vildsvin hind") ignoreras. */
const TYPE_FAMILIES: Record<string, string[]> = {
  kalv: ['alg', 'kronhjort', 'dovhjort', 'radjur'], ko: ['alg', 'kronhjort', 'dovhjort'], kviga: ['alg'],
  tjur: ['alg', 'dovhjort', 'kronhjort'], hind: ['kronhjort', 'dovhjort'], hjort: ['kronhjort', 'dovhjort'],
  bock: ['radjur', 'dovhjort'], get: ['radjur'], kid: ['radjur'], spets: ['alg', 'kronhjort', 'dovhjort', 'radjur'],
  skovel: ['dovhjort'], halvskovel: ['dovhjort'], kapitalhjort: ['kronhjort'], medaljbock: ['radjur'], spetstjur: ['alg'],
  sugga: ['vildsvin'], galt: ['vildsvin'], årsgris: ['vildsvin'], kulting: ['vildsvin'], smågris: ['vildsvin'],
  bagge: ['mufflon'], kapitalbagge: ['mufflon'], tacka: ['mufflon'], lamm: ['mufflon'],
};

const SPECIES_INDEX = new Map<string, string>();
for (const [fam, fs] of SPECIES_WORDS) for (const f of fs) SPECIES_INDEX.set(f, fam);
const TYPE_INDEX = new Map<string, Typ>();
for (const [fs, t] of TYPES) for (const f of fs) TYPE_INDEX.set(f, t);

type Tok = { family?: string; typ?: Typ; bare?: boolean };

function parseToken(w: string): Tok | null {
  const fam = SPECIES_INDEX.get(w);
  if (fam) return { family: fam };
  const typ = TYPE_INDEX.get(w);
  if (typ) return { typ };
  if (BARE_PREFIX[w]) return { family: BARE_PREFIX[w], bare: true };
  // Sammansättning: förled (+ fogens s) + typord: "dovkalv", "algtjuren", "radjurskid", "vildsvinsgalt".
  for (const [p, f] of PREFIXES) {
    if (!w.startsWith(p) || w.length === p.length) continue;
    let rest = w.slice(p.length);
    let t = TYPE_INDEX.get(rest);
    if (!t && rest.startsWith('s')) { rest = rest.slice(1); t = TYPE_INDEX.get(rest); }
    if (t && (TYPE_FAMILIES[t.typ]?.includes(f) ?? true)) return { family: f, typ: t };
  }
  // Övriga arter + kön/ålder: "rävhona", "grävlingunge".
  for (const [f, fs] of SPECIES_WORDS) {
    const base = fs[0];
    if (!w.startsWith(base) || w.length === base.length) continue;
    const t = TYPE_INDEX.get(w.slice(base.length));
    if (t && (t.typ === 'hane' || t.typ === 'hona' || t.typ === 'unge')) return { family: f, typ: t };
  }
  return null;
}

const NUMBERS: Record<string, number> = {
  tva: 2, tre: 3, fyra: 4, fem: 5, sex: 6, sju: 7, atta: 8, nio: 9, tio: 10, elva: 11, tolv: 12, fjorton: 14, sexton: 16, arton: 18, tjugo: 20,
};
/** "tiotaggare", "10 taggar", "sex taggar" → antal taggar. */
function tagCount(n: string): number | null {
  const m = n.match(/(?:^| )(\d{1,2}|[a-z]+?) ?taggar(?:e|en|na)?(?= |$)/);
  if (!m) return null;
  const v = /^\d+$/.test(m[1]) ? Number(m[1]) : NUMBERS[m[1]];
  return v ?? null;
}

function finish(family: string, typ: Typ | undefined, n: string): HeardGame {
  const g: HeardGame = { family, label: LABELS[family] ?? family };
  if (typ && typ.typ !== 'hjort' && (TYPE_FAMILIES[typ.typ]?.includes(family) ?? true)) {
    g.typ = typ.typ;
    if (typ.kon) g.kon = typ.kon;
    if (typ.alder) g.alder = typ.alder;
    const sub = typ.sub?.[family];
    if (sub) g.subcat = sub;
  }
  // Taggar: älgtjur och råbock delas in efter antal taggar.
  const tags = tagCount(n);
  if (tags != null && (g.kon === 'Hane' || !g.typ)) {
    if (family === 'alg') { g.subcat = tags > 10 ? 'Tjur >10 taggar' : tags >= 4 ? 'Tjur 4–10 taggar' : 'Tjur 2–4 taggar'; g.kon = 'Hane'; g.alder = 'Vuxet'; g.typ ??= 'tjur'; }
    if (family === 'radjur') { g.subcat = tags >= 6 ? 'Råbock 6-taggare' : tags >= 4 ? 'Råbock 4-taggare' : 'Råbock spets'; g.kon = 'Hane'; g.alder = 'Vuxet'; g.typ ??= 'bock'; }
    if (family === 'kronhjort' && tags < 10) { g.subcat = 'Hjort under 10 taggar'; g.kon = 'Hane'; g.alder = 'Vuxet'; g.typ ??= 'hjort'; }
  }
  return g;
}

/** Ord som får stå mellan art och typ: "älg, en stor tjur", "dov – den var kalv". */
const FILLER = new Set(['en', 'ett', 'den', 'det', 'stor', 'storre', 'liten', 'ung', 'gammal', 'var', 'ar', 'som', 'typ', 'nog', 'eh', 'och']);

/**
 * Första viltet i en rad. Typen kan sitta ihop med arten ("dovkalv", "älgtjuren"), stå efter den
 * ("dov kalv", "vildsvin årsgris", "kron hind") eller räcka själv ("sugga", "kulting").
 * Ensamma "kalv", "hind", "bock" och "hjort" ger inget (arten är otydlig).
 */
export function parseHeardGame(text: string): HeardGame | null {
  const n = heardNorm(text);
  const words = n.split(' ').filter(Boolean);
  for (let i = 0; i < words.length; i++) {
    const t = parseToken(words[i]);
    if (!t) continue;
    if (t.family) {
      if (t.typ) return finish(t.family, t.typ, n);
      // Art följd av typ: "dov kalv", "vildsvin, årsgris", "vildsvin tror det var en sugg".
      for (let j = i + 1; j < Math.min(words.length, i + 7); j++) {
        const nx = parseToken(words[j]);
        if (nx?.family && !nx.bare && nx.family !== t.family) break; // nästa djur
        if (nx?.typ && nx.typ.typ !== 'hjort' && (!nx.family || nx.family === t.family)
          && (TYPE_FAMILIES[nx.typ.typ]?.includes(t.family) ?? true)) return finish(t.family, nx.typ, n);
        if (t.bare && !FILLER.has(words[j])) break; // "rå" / "kron" måste stå intill typen
      }
      if (t.bare) continue; // "rå" / "kron" utan typ efter är inget vilt
      return finish(t.family, undefined, n);
    }
    if (t.typ) {
      // Typ före arten: "kalv av älg", "en hind, dovhjort".
      for (let j = i + 1; j < Math.min(words.length, i + 4); j++) {
        const nx = parseToken(words[j]);
        if (nx?.family && !nx.typ && !nx.bare && (TYPE_FAMILIES[t.typ.typ]?.includes(nx.family) ?? true)) return finish(nx.family, t.typ, n);
        if (!FILLER.has(words[j]) && words[j] !== 'av' && words[j] !== 'pa') break;
      }
      const fam = TYPE_ONLY_FAMILY[t.typ.typ];
      if (fam) return finish(fam, t.typ, n);
    }
  }
  return null;
}

// Fällt/dött: "den ligger", "är död", "stupade", "fälld", "är nere". ("Jag ligger på passet" är inte dött.)
const DEAD_RE = /(?:^| )(liggande|lag kvar|dod|dott|doda|dog|stupade|stupat|stupad|stupar|fallt|falld|fallda|fallde|nedlagd|nedlagt|nerlagd|nerlagt|lagt ned|lagt ner|avlivad|avlivat|avlivade|ihjal|dodskjuten|dodskjutet|tog ner|knackt|knackte|(?:ar|var|den|ligger|e) nere|bekraftat fallt)(?= |$)/;
const LIGGER_RE = /(?:^|(?:^| )(?!(?:jag|vi|du|ni|man|som|och)(?: |$))\p{L}+ )ligger(?= |$)/u;
const NOT_DEAD_RE = /(?:^| )(?:(?:inte|ej|aldrig) (?:\p{L}+ )?(?:dod|ligger|nere|fallt|falld|stupade)|(?:ligger|dog|stupade|ar nere) (?:inte|ej))(?= |$)/u;
// Skadskjutet: uttryckligen, eller skott och "den gick", "gick vidare" …
const WOUNDED_RE = /(?:^| )(skadskjut\p{L}*|skadeskjut\p{L}*|skadad|skadat|eftersok\p{L}*|blodspar\p{L}*|blod pa|ringa eftersok|hund pa spar)(?= |$)/u;
const WENT_RE = /(?:^| )(den gick|det gick|gick vidare|gick undan|gick ivag|sprang vidare|sprang ivag|stack ivag|stack|for vidare|kom undan|drog ivag|forsvann)(?= |$)/;
const SHOT_RE = /(?:^| )(skot|skots|skjut\p{L}*|sjkuten|skott\p{L}*|paskjut\p{L}*|traff\p{L}*|prick\p{L}*|small|smallde|skjot|panga|pangade|bom|bommade|bommat)(?= |$)/u;
const OBS_RE = /(?:^| )(obs|ser|sag|sedd|sett|observation|observerade?|passerade?|passerar|sprang|springer|ror sig|rorde sig|syntes|syns|hann se|noterade?|markte|drog forbi|kommer|kom|pa vag|hos mig|vid mig|star|stod|gar|gick)(?= |$)/;

/**
 * Vad som hände med viltet i raden: fällt ("skjuten dov, den ligger"), skadskjutet ("skadskjuten",
 * "sköt men den gick"), skott ("skjuten dov", "skott mot vildsvinet") eller obs. null = inget sagt.
 */
export function heardOutcome(text: string): HeardOutcome | null {
  const n = heardNorm(text);
  const shot = SHOT_RE.test(n);
  const dead = (DEAD_RE.test(n) || LIGGER_RE.test(n)) && !NOT_DEAD_RE.test(n);
  if (dead) return 'fallt';
  if (WOUNDED_RE.test(n) || (shot && (WENT_RE.test(n) || NOT_DEAD_RE.test(n)))) return 'skadskjutet';
  if (shot) return 'skott';
  if (OBS_RE.test(n)) return 'obs';
  return null;
}

/** "Dovhjort (dovkalv)", "Älg (tjur)", "Vildsvin (sugga)", "Rådjur". */
export function heardGameText(g: HeardGame): string {
  const typ = g.subcat ? g.subcat.toLowerCase() : g.typ;
  return typ ? `${g.label} (${typ})` : g.label;
}

/**
 * Skjutmenyns underkategorier som passar det som hördes: hördes en underkategori bara den,
 * annars de med samma kön/ålder ("älgtjur" → tjurarna, "dovbock" → spets och skovlar).
 */
export function matchingSubcats(g: HeardGame, subcats: string[]): string[] {
  if (g.subcat && subcats.includes(g.subcat)) return [g.subcat];
  if (!g.kon && !g.alder) return subcats;
  const fit = subcats.filter(s => {
    const p = parseHeardGame(`${g.label} ${s}`);
    if (!p) return true;
    if (g.kon && p.kon && p.kon !== g.kon) return false;
    if (g.alder && p.alder && p.alder !== g.alder) return false;
    return true;
  });
  return fit.length ? fit : subcats;
}
